import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import type { Direction } from '../../../lib/types';
import { scoreTo100 } from '../../../lib/exitScoring';
import { getScoreLabel, getScoreTextColor } from '../../../lib/constants';
import { useFeaturedVenues, type FeaturedVenue } from '../../../hooks/useFeaturedVenues';

// Display order per mockup: W, N, E, S
const DISPLAY_ORDER: Direction[] = ['W', 'N', 'E', 'S'];

function ScoreCell({ venue, dir }: { venue: FeaturedVenue; dir: Direction }) {
  const s = venue.scores[dir];
  const has = s && s.score > 0;
  const score = has ? scoreTo100(s.score) : null;
  const color = has ? getScoreTextColor(s.score) : '#9ca3af';
  const label = has ? getScoreLabel(s.score) : '—';
  return (
    <div className="flex flex-col items-center py-2">
      <span className="text-[10px] font-bold text-gray-400">{dir}</span>
      <span className="text-lg font-extrabold leading-tight" style={{ color }}>
        {score ?? '—'}
      </span>
      <span className="text-[9px] font-semibold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

function Card({ venue }: { venue: FeaturedVenue }) {
  return (
    <Link
      to={`/venues/${venue.slug}`}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="relative h-32 overflow-hidden bg-gray-100">
        {venue.image_url ? (
          <img
            src={venue.image_url}
            alt={venue.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: 'linear-gradient(135deg,#e2e8f0,#cbd5e1)' }}
          />
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-gray-900 truncate">{venue.name}</h3>
        <p className="text-sm text-gray-400 truncate">
          {venue.city}
          {venue.state ? `, ${venue.state}` : ''}
        </p>

        <div className="mt-3 grid grid-cols-4 rounded-xl border border-gray-100 bg-gray-50/60">
          {DISPLAY_ORDER.map((d) => (
            <ScoreCell key={d} venue={venue} dir={d} />
          ))}
        </div>

        <span
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold"
          style={{ color: '#17A24A' }}
        >
          View parking
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

export default function VenueScoreCards() {
  const { venues, loading } = useFeaturedVenues(4);

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            Find the best garage for your destination
          </h2>
          <Link
            to="/venues"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold"
            style={{ color: '#17A24A' }}
          >
            View all venues
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-72 rounded-2xl border border-gray-200 bg-gray-50 animate-pulse" />
            ))}
          </div>
        ) : venues.length === 0 ? (
          <p className="text-gray-400">No scored venues available yet.</p>
        ) : (
          <div className="relative">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {venues.map((v) => (
                <Card key={v.id} venue={v} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 sm:hidden">
          <Link to="/venues" className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#17A24A' }}>
            View all venues
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
